import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

export type BasicFeeType = 'RANGE' | 'FIXED';

export interface RangeFeeItem {
  id?: number;
  rangeMin: number;
  rangeMax: number;
  rate: number;
}

export interface FixedFeeUser {
  userId: number;
  email?: string;
  companyName?: string;
}

export interface FixedFeeItem {
  id?: number;
  rate: number;
  users?: FixedFeeUser[];
  userIds?: number[];
}

export interface BasicFeePolicy {
  settlementYearMonth: string;
  rangeFees?: RangeFeeItem[];
  fixedFees?: FixedFeeItem[];
  policyConfigValid?: boolean;
  /** 신규 응답 */
  configuredAt?: string;
  /** 레거시 응답 필드명 */
  updatedAt?: string;
}

/** 레거시 `baseFeeRate.js` 목록·유저 히스토리 행 */
export interface BasicFeeHistoryItem {
  userId: number;
  email: string;
  companyName: string;
  ceoName: string;
  basicFeeType: BasicFeeType;
  prescriptionTargetAmount?: number | null;
  prescriptionFeeRate?: number | null;
  settlementTargetAmount?: number | null;
  settlementFeeRate?: number | null;
  /** 유저별 히스토리 */
  settlementYearMonth?: string;
  policyId?: number;
}

/** 레거시 `detailFeeRate.js` 목록 행 — 사용자 단위 집계 */
export interface DetailFeeGroupItem {
  userId: number;
  /** 레거시 응답 필드 */
  userEmail?: string;
  /** 일부 응답·폴백 */
  email?: string;
  companyName: string;
  ceoName?: string;
  basicFeeType: BasicFeeType;
  detailClinicCount?: number;
  detailPharmaCount?: number;
  detailDrugCount?: number;
}

export interface DetailFeeGroupRegisterRequest {
  userId: number;
  clinicId: number;
  clinicType: 'HOSPITAL' | 'CLINIC';
  basicFeeType?: BasicFeeType;
}

export interface DetailFeeGroupClinic {
  clinicId: number;
  clinicName: string;
  clinicType: string;
  detailFeeConfigured?: boolean;
}

export const feeRateService = {
  getBasicFeeHistory: async (
    params: {
      settlementYearMonth?: string;
      searchKeyword?: string;
      basicFeeType?: BasicFeeType;
    },
    pageable: Pageable,
  ): Promise<PageResponse<BasicFeeHistoryItem>> => {
    const response = await apiClient.get(createApiPath.feeRates.history(), {
      params: { ...params, page: pageable.page, size: pageable.size },
    });
    return response.data;
  },

  getBasicFeePolicy: async (
    settlementYearMonth: string,
  ): Promise<BasicFeePolicy | null> => {
    try {
      const response = await apiClient.get(createApiPath.feeRates.policy(), {
        params: { settlementYearMonth },
      });
      return response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) return null;
      throw error;
    }
  },

  copyBasicFeePolicy: async (fromYearMonth: string, toYearMonth: string) => {
    const response = await apiClient.post(createApiPath.feeRates.policyCopy(), {
      fromYearMonth,
      toYearMonth,
    });
    return response.data as BasicFeePolicy;
  },

  registerBasicFeePolicy: async (data: BasicFeePolicy) => {
    const response = await apiClient.post(createApiPath.feeRates.policy(), data);
    return response.data as BasicFeePolicy;
  },

  getBasicFeeUserHistory: async (
    userId: number,
    pageable: Pageable,
  ): Promise<PageResponse<BasicFeeHistoryItem>> => {
    const response = await apiClient.get(createApiPath.feeRates.historyUser(), {
      params: { userId, page: pageable.page, size: pageable.size },
    });
    return response.data;
  },

  getDetailFeeGroups: async (
    params: {
      searchKeyword?: string;
      basicFeeType?: BasicFeeType;
      detailFeeConfiguredOnly?: boolean;
      sort?: string;
    },
    pageable: Pageable,
  ): Promise<PageResponse<DetailFeeGroupItem>> => {
    const response = await apiClient.get(createApiPath.feeRates.detailGroups(), {
      params: {
        ...params,
        page: pageable.page,
        size: pageable.size,
      },
    });
    return response.data;
  },

  getSettingTargetClinics: async (
    userId: number,
  ): Promise<DetailFeeGroupClinic[]> => {
    const response = await apiClient.get(
      createApiPath.feeRates.detailGroupClinics(),
      { params: { userId } },
    );
    return response.data;
  },

  registerDetailFeeGroup: async (data: DetailFeeGroupRegisterRequest) => {
    const response = await apiClient.post(
      createApiPath.feeRates.detailGroups(),
      data,
    );
    return response.data;
  },

  removeDetailFeeGroup: async (detailGroupId: number) => {
    await apiClient.delete(createApiPath.feeRates.detailGroup(detailGroupId));
  },

  getHospitalDetailFeeGroup: async (params: {
    userId: number;
    clinicId: number;
    basicFeeType: BasicFeeType;
  }) => {
    try {
      const response = await apiClient.get(
        createApiPath.feeRates.hospitalDetail(),
        { params },
      );
      return response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) return null;
      throw error;
    }
  },

  getClinicDetailFeeGroup: async (params: {
    userId: number;
    clinicId: number;
    basicFeeType: BasicFeeType;
  }) => {
    try {
      const response = await apiClient.get(createApiPath.feeRates.clinicDetail(), {
        params,
      });
      return response.data;
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      if (status === 404) return null;
      throw error;
    }
  },

  saveHospitalDrugRules: async (data: Record<string, unknown>) => {
    const response = await apiClient.post(
      createApiPath.feeRates.hospitalDrugRules(),
      data,
    );
    return response.data;
  },

  saveClinicPharmaRates: async (data: Record<string, unknown>) => {
    const response = await apiClient.post(
      createApiPath.feeRates.clinicPharmaRates(),
      data,
    );
    return response.data;
  },

  saveClinicDrugRates: async (data: Record<string, unknown>) => {
    const response = await apiClient.post(
      createApiPath.feeRates.clinicDrugRates(),
      data,
    );
    return response.data;
  },
};
